import React from 'react';
import moment from 'moment';
import * as uuid from 'uuid';
import startCase from 'lodash/startCase';
import { getFormattedDate, isDateString } from '../../helpers/dateTime';
import { t, translateOptionLabel } from '../../i18n';

const dateRegExp = /^20\d\d-\d\d-\d\d.\d\d:\d\d:\d\d/; // matches 2022-10-10 20:30:40

export function formatPropName(prop) {
  return startCase(prop.toString().replace(/_/g, ' '));
}

function isValidDate(date) {
  return typeof date === 'string' && dateRegExp.test(date) && moment(date).isValid();
}

function getMetaListItem(prop, value) {
  return (
    <li key={uuid.v4()}>
      <b>{prop && `${formatPropName(prop)}: `}</b>
      {Array.isArray(value) ? <ul>{value}</ul> : <span>{value}</span>}
    </li>
  );
}

function formatBoolean(value) {
  return t(`dash.common.constants.${value.toString()}`);
}

function formatObject(object) {
  const entries = Object.keys(object);

  // eslint-disable-next-line no-use-before-define
  return entries.map(key => getMetaListItem(key, getMetaItemValue(object[key], key)));
}

function formatLink(value) {
  return (
    <a target="_blank" rel="noopener noreferrer" href={value}>
      {value}
    </a>
  );
}

function formatArray(list) {
  return list.map(item => {
    let arrayItemValue = '';

    if (item && item.count) {
      arrayItemValue = `[${item.count} x ${item.position} (${translateOptionLabel('level', item.level)})]`;
    } else {
      // eslint-disable-next-line no-use-before-define
      arrayItemValue = getMetaItemValue(item);
    }

    return getMetaListItem(null, arrayItemValue);
  });
}

function getMetaItemValue(value, prop) {
  if (['client'].includes(prop)) {
    return value;
  }

  if (prop === 'eta') {
    const [dateValue, earlyValue] = value.split('. ');

    return `${
      dateValue ? getFormattedDate({ date: dateValue, formatKey: 'datetime_date_numeric' }) : ''
    }. ${earlyValue}`;
  }

  if (Array.isArray(value)) {
    return formatArray(value);
  }

  if (value instanceof Object) {
    return formatObject(value);
  }

  if (typeof value === 'boolean') {
    return formatBoolean(value);
  }

  if (/^(http|https):\/\/.*$/.test(value)) {
    return formatLink(value, prop);
  }

  if (isDateString(value)) {
    return value ? getFormattedDate({ date: value, formatKey: 'date_numeric_long' }) : '';
  }

  if (isValidDate(value)) {
    return value ? getFormattedDate({ date: value, formatKey: 'datetime_date_numeric' }) : '';
  }

  if (value === null || value === undefined || value === '') {
    return t('dash.common.aliases.n/a');
  }

  if (value && typeof value === 'string' && /\(tier_\d\)/g.test(value)) {
    return value.replace(/\(tier_\d\)/g, substr => {
      const tier = substr.replace('(', '').replace(')', '');

      return translateOptionLabel('level', tier, t('dash.common.aliases.n/a'));
    });
  }

  return value;
}

export default function getMeta(meta) {
  let listItems = [];

  if (Array.isArray(meta)) {
    listItems = meta.map((value, prop) => getMetaListItem(prop + 1, getMetaItemValue(value, prop + 1)));
  } else {
    listItems = Object.entries(meta || {}).map(([prop, value]) => getMetaListItem(prop, getMetaItemValue(value, prop)));
  }

  return <ul className="log-meta">{listItems}</ul>;
}
